/* eslint-disable max-len */
import React, { useEffect } from 'react';
import {
  Chip, Grid, Switch, Typography, FormControlLabel, Paper, Button,
} from '@mui/material';
import { DateTime } from 'luxon';
import { Box } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { AuditLog, ReportType } from '../../core/api/clients';
import FormPaper from '../../components/FormPaper';
import useGetAuditLogs from '../../core/api/hooks/common/useGetAuditLogs';
import { useIncidentReportData } from '../../core/context/IncidentReportDataContext';
import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import FormHeader from '../../components/FormHeader';
import FormTitle from '../../components/FormTitle';
import IncidentStatusChip from '../../components/incident/IncidentStatusChip';

function IncidentAuditLogs() {
  const { incidentReport, updateStatus, update } = useIncidentReportData();
  const { data, refetch } = useGetAuditLogs(incidentReport.id!, ReportType.Incident);
  const [dateGroup, setDateGroup] = React.useState<[string, AuditLog[]][]>();
  const [groupByKey, setGroupByKey] = React.useState('date');
  const authorizedFormData = useAuthorizedFormData();
  const getTimeElement = (date: string) => {
    const convertedDate: DateTime = DateTime.fromISO(date);
    return convertedDate.toFormat('HH:mm');
  };
  const getFullDate = (date: string) => {
    const convertedDate: DateTime = DateTime.fromISO(date);
    return convertedDate.toFormat('DD @ HH:mm');
  };
  useEffect(() => {
    refetch();
  }, [updateStatus, update]);

  useEffect(() => {
    if (data) {
      const grouping = data.sort((a, b) => Date.parse(b.lastModifiedOn!) - Date.parse(a.lastModifiedOn!)).reduce((group: { [key: string]: AuditLog[] }, item) => {
        const propName: DateTime = DateTime.fromISO(item.lastModifiedOn!);
        let dateString = item.key;
        if (groupByKey === 'date') {
          dateString = propName.toFormat('LLL dd yyyy');
        }

        if (dateString) {
          if (!group[dateString]) {
            // eslint-disable-next-line no-param-reassign
            group[dateString] = [];
          }
          group[dateString].push(item);
        }
        return group;
      }, {});
      setDateGroup(Object.entries(grouping));
    }
  }, [data, groupByKey]);

  const getModificationColor = (modificationType: string | null | undefined) => {
    // eslint-disable-next-line default-case
    switch (modificationType) {
      case 'Added':
        return 'success';
      case 'Modified':
        return 'warning';
      case 'Deleted':
        return 'error';
    }
    return 'primary';
  };

  const handleGroupingChange = (e: React.ChangeEvent<HTMLElement>, checked: boolean) => {
    if (checked) {
      setGroupByKey('date');
    } else {
      setGroupByKey('field');
    }
  };
  return (
    <FormPaper color={authorizedFormData.metadata?.color}>
      <FormHeader>
        <Grid display="flex" container spacing={1} paddingX={1}>
          <Grid item xs={2} justifyContent="flex-start">
            <FormTitle
              title="Incident"
              subtitle={[]}
            />
          </Grid>
          <Grid display="flex" item xs={8} justifyContent="center">
            <Typography variant="h4">{incidentReport.title ?? ''}</Typography>
          </Grid>
          <Grid display="flex" item xs={2} justifyContent="flex-end">
            <IncidentStatusChip
              status={incidentReport.status}
            />
          </Grid>
        </Grid>
      </FormHeader>
      <Box display="flex" justifyContent="flex-end" padding={1}>
        <FormControlLabel control={<Switch defaultChecked onChange={handleGroupingChange} />} label={`By ${groupByKey}`} />
      </Box>
      <Box display="flex" flexDirection="column" paddingX={1}>
        {dateGroup?.map((group) => (
          <Paper key={group[0]} elevation={1} sx={{ padding: 1 }}>
            <Box marginBottom={1}>
              <Typography variant="h5">{group[0]}</Typography>
            </Box>
            <Box>
              {group[1].map((record, index) => (
                <Grid key={record.id} container bgcolor={index % 2 === 0 ? 'lightGray' : 'white'} padding={1}>
                  <Grid item md={1}>
                    <Chip label={record.modificationType} color={getModificationColor(record.modificationType)} />
                  </Grid>
                  <Grid container item md={2}>
                    <Typography marginRight={1}>Field:</Typography>
                    <Typography fontWeight="bold">{record.key}</Typography>
                  </Grid>
                  <Grid container item md={2}>
                    <Typography marginRight={1}>Original Value:</Typography>
                    <Typography fontWeight="bold">{record.originalValue}</Typography>
                  </Grid>
                  <Grid container item md={2}>
                    <Typography marginRight={1}>New Value:</Typography>
                    <Typography fontWeight="bold">{record.newValue}</Typography>
                  </Grid>
                  <Grid container item md={2}>
                    <Typography marginRight={1}>Modified By: </Typography>
                    <Typography marginRight={1} fontWeight="bold">{record.lastModifiedBy}</Typography>
                  </Grid>
                  <Grid container item md={3}>
                    <Typography marginRight={1}>Modified On:</Typography>
                    <Typography fontWeight="bold">{groupByKey === 'field' ? getFullDate(record.lastModifiedOn!) : getTimeElement(record.lastModifiedOn!)}</Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    </FormPaper>
  );
}
export default IncidentAuditLogs;
